import React, { useEffect, useState } from 'react';
import { Bell, Send, Users, Clock, CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const PushNotifications = () => {
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [stats, setStats] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    body: '',
    url: '/',
    target: 'all'
  });

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await api.get('/admin/push/stats');
      // Also fetch recent broadcasts
      const broadcastsRes = await api.get('/admin/push/broadcasts?limit=10');
      setStats({
        ...response.data,
        recent_notifications: broadcastsRes.data
      });
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendBroadcast = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.body) {
      toast.error('Please fill in title and body');
      return;
    }

    setSending(true);
    try {
      const response = await api.post('/admin/push/send', {
        ...formData,
        type: 'promotion'
      });
      toast.success(response.data.message);
      setFormData({ title: '', body: '', url: '/', target: 'all' });
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send notification');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-6 flex items-center justify-center">
        <div className="text-2xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 px-6" data-testid="push-notifications-page">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl md:text-4xl font-bold">
            <Bell className="inline w-8 h-8 mr-3" />
            Push Notifications
          </h1>
          <button
            onClick={fetchStats}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            title="Refresh"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="glass-card p-6 text-center">
            <Users className="w-8 h-8 text-primary mx-auto mb-2" />
            <p className="text-3xl font-bold">{stats?.users_with_push || 0}</p>
            <p className="text-muted-foreground">Users Subscribed</p>
          </div>
          <div className="glass-card p-6 text-center">
            <Bell className="w-8 h-8 text-blue-500 mx-auto mb-2" />
            <p className="text-3xl font-bold">{stats?.total_subscriptions || 0}</p>
            <p className="text-muted-foreground">Total Subscriptions</p>
          </div>
          <div className="glass-card p-6 text-center">
            <Send className="w-8 h-8 text-green-500 mx-auto mb-2" />
            <p className="text-3xl font-bold">{stats?.total_broadcasts || 0}</p>
            <p className="text-muted-foreground">Broadcasts Sent</p>
          </div>
          <div className="glass-card p-6 text-center">
            <Clock className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
            <p className="text-3xl font-bold">{stats?.total_notifications || 0}</p>
            <p className="text-muted-foreground">Total Notifications</p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Send Notification Form */}
          <div className="glass-card p-6">
            <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
              <Send className="w-5 h-5" />
              Send Broadcast
            </h2>
            
            <form onSubmit={handleSendBroadcast} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Notification title"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary"
                  data-testid="notification-title"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Body *</label>
                <textarea
                  value={formData.body}
                  onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                  placeholder="Notification message"
                  rows={3}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary"
                  data-testid="notification-body"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Click URL</label>
                <input
                  type="text"
                  value={formData.url}
                  onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                  placeholder="/"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary"
                  data-testid="notification-url"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Target Audience</label>
                <select
                  value={formData.target}
                  onChange={(e) => setFormData({ ...formData, target: e.target.value })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary"
                  data-testid="notification-target"
                >
                  <option value="all">All Subscribers</option>
                  <option value="active">Active Users (Last 7 days)</option>
                </select>
              </div>
              
              <button
                type="submit"
                disabled={sending}
                className="w-full btn-primary flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="send-notification-btn"
              >
                <Send className="w-5 h-5" />
                {sending ? 'Sending...' : 'Send Notification'}
              </button>
            </form>
          </div>

          {/* Recent Notifications */}
          <div className="glass-card p-6">
            <h2 className="text-xl font-semibold mb-6">Recent Broadcasts</h2>
            
            {stats?.recent_notifications?.length > 0 ? (
              <div className="space-y-4 max-h-[500px] overflow-y-auto">
                {stats.recent_notifications.map((notification, idx) => (
                  <div key={idx} className="p-4 bg-white/5 rounded-xl">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-semibold">{notification.title}</h3>
                      <span className="text-xs text-muted-foreground">
                        {new Date(notification.sent_at).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">{notification.body}</p>
                    <div className="flex items-center gap-4 text-xs">
                      <span className="flex items-center gap-1 text-green-500">
                        <CheckCircle className="w-3 h-3" />
                        {notification.success_count} sent
                      </span>
                      <span className="flex items-center gap-1 text-muted-foreground">
                        <Users className="w-3 h-3" />
                        {notification.total_sent} total
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-muted-foreground py-8">
                No notifications sent yet
              </p>
            )}
          </div>
        </div>

        {/* Setup Instructions */}
        <div className="glass-card p-6 mt-8">
          <h2 className="text-xl font-semibold mb-4">Setup Instructions</h2>
          <div className="space-y-3 text-sm text-muted-foreground">
            <p>
              <strong>1.</strong> Users need to enable notifications in their browser when prompted.
            </p>
            <p>
              <strong>2.</strong> Notifications work on Chrome, Firefox, Edge, and Safari (iOS 16.4+).
            </p>
            <p>
              <strong>3.</strong> For production, set up VAPID keys in environment variables:
            </p>
            <code className="block p-3 bg-black/30 rounded-lg text-xs font-mono">
              VAPID_PUBLIC_KEY=your_public_key<br/>
              VAPID_PRIVATE_KEY=your_private_key
            </code>
            <p className="text-xs">
              Generate keys at: <a href="https://vapidkeys.com/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">vapidkeys.com</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PushNotifications;
